import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import PaymentBreakdown from "components/Screens/CustomerOnboarding/PaymentBreakdown";
import Layout from "components/Screens/CustomerOnboarding//Layout";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";
import useDeviceInfo from "components/Hooks/useDeviceInfo";
import useView from "components/Hooks/useViewHook";
import CustomChip from "components/common/Chip";

import { getCustomerOnboardingIncompleteStepRoute } from "components/Screens/CustomerOnboarding/helpers";

import { getUserAdditionalInfo, getLoadingAdditionalInfo } from "selectors/user";
import { getPlatformConfig } from "selectors/platformConfig";

import {
  getClientSecretApi,
  skipCustomerOnboardingStepApi,
  customerOnboardingAddPayment,
  createFinancialConnectionsSetupIntent,
} from "services/customer";

import { showSnackbar } from "store/actions/Utility";

import AccountBalanceIcon from "@mui/icons-material/AccountBalanceOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCardOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  CUSTOMER_ONBOARDING_CALENDLY_MEETING_ROUTE,
  CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE,
} from "constants/routes";
import { ONBOARDING_PAYMENT_VIEW } from "components/Screens/CustomerOnboarding/constants";

import "./AddPaymentMethod.scss";
import AddCreditCard from "components/Screens/CustomerOnboarding/AddPaymentMethod/AddCreditCard";
import AddAchPayment from "components/Screens/CustomerOnboarding/AddPaymentMethod/AddAchPayment";
import PageLoader from "components/common/PageLoader";
import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";

function AddPaymentMethod() {
  const [loading, setLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [renderView, setRenderView] = useState(ONBOARDING_PAYMENT_VIEW.ADD_PAYMENT_METHOD);
  const [iosPadding, setIosPadding] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isIOS } = useDeviceInfo();
  const { stripeFc = false } = useFlags();
  const userInfo = useSelector(getUserAdditionalInfo);
  const userInfoLoading = useSelector(getLoadingAdditionalInfo);
  const platformConfig = useSelector(getPlatformConfig);
  const { isMobile } = useView();

  const { customerId, onboardingProgress = {}, creditCardSettings = {} } = userInfo;
  const { allowCreditCard = false } = creditCardSettings ?? {};
  const { steps = [], onboardingProgressId = null } = onboardingProgress ?? {};
  const paymentStep = steps.find((step) => step.slug === "add-payment");
  const { skippable = false, slug = "" } = paymentStep ?? {};
  const isStepSkippable = skippable ?? false;

  const { payload: platformPayload = {}, ok: isPlatformConfigLoaded = false } = platformConfig || {};

  useEffect(() => {
    if (allowCreditCard && customerId) {
      const fetchClientSecret = async () => {
        try {
          const data = await getClientSecretApi(customerId, dispatch);
          const secret = data.client_secret || data.clientSecret;
          setClientSecret(secret);
        } catch (error) {
          setSnackbarMessage("Failed to initialize payment. Please try again.");
        }
      };

      fetchClientSecret();
    }
  }, [customerId, allowCreditCard]);

  const onSkip = async () => {
    const { payload = {} } = await skipCustomerOnboardingStepApi({ onboardingProgressId, stepSlug: slug }, dispatch);
    const { success = false } = payload ?? {};
    if (success) {
      navigate(CUSTOMER_ONBOARDING_CALENDLY_MEETING_ROUTE);
    }
  };

  useEffect(() => {
    if (paymentStep?.completed_at !== null) {
      const customerOnboardingIncompleteStepRoute = getCustomerOnboardingIncompleteStepRoute(onboardingProgress);
      if (customerOnboardingIncompleteStepRoute) {
        navigate(customerOnboardingIncompleteStepRoute);
      }
    }
  }, [navigate, onboardingProgress, paymentStep?.completed_at]);

  // Stripe Financial Connections handler (Connect with Stripe option)
  const handleConnectViaStripeFC = useCallback(async () => {
    try {
      if (!platformPayload.STRIPE_PUBLISHABLE_KEY) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Stripe configuration is missing. Please try again later.",
          })
        );
        return;
      }

      setLoading(true);

      const stripe = await loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
      const fcResponse = await createFinancialConnectionsSetupIntent(customerId);
      const fcClientSecret = fcResponse?.clientSecret || fcResponse?.client_secret;

      if (!fcClientSecret) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: "Failed to initialize Stripe. Please try again.",
          })
        );
        setLoading(false);
        return;
      }

      setLoading(false);

      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
      const userEmail = loggedInUser?.email || "";
      const userName = loggedInUser?.name || loggedInUser?.firstName || "Customer";

      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: fcClientSecret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              name: userName,
              email: userEmail,
            },
          },
        },
      });

      if (error) {
        dispatch(
          showSnackbar({
            dispatch,
            type: AlertType.error,
            message: error.message || "Failed to connect bank account",
          })
        );
        return;
      }

      if (setupIntent && setupIntent.payment_method) {
        setLoading(true);
        const body = {
          setupIntentId: setupIntent.id,
          paymentMethodType: "ACH_STRIPE_FC",
        };
        const response = await customerOnboardingAddPayment(body, customerId);
        if (response?.success) {
          dispatch(
            showSnackbar({
              dispatch,
              type: AlertType.success,
              message: "Payment method added successfully!",
            })
          );
          const { chargeInitiated = true, scheduledAt = null } = response ?? {};
          setTimeout(() => {
            navigate(CUSTOMER_ONBOARDING_PAYMENT_SUCCESS_ROUTE, {
              state: { chargeInitiated, scheduledAt },
            });
          }, 2000);
        }
        setLoading(false);
      }
    } catch (error) {
      const errorMessage = error[0]?.description || error?.message || "Something went wrong, please try again";
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: errorMessage,
        })
      );
      setLoading(false);
    }
  }, [platformPayload.STRIPE_PUBLISHABLE_KEY, customerId, dispatch, navigate]);

  const handleConnectWithStripeClick = () => {
    if (stripeFc) {
      handleConnectViaStripeFC();
    } else {
      // When stripeFc is false, go to ACH view which has Plaid integration
      setRenderView(ONBOARDING_PAYMENT_VIEW.ACH);
    }
  };

  const renderCreditCardForm = () => {
    let stripePromise = null;
    if (isPlatformConfigLoaded) {
      stripePromise = loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
    }

    if (!clientSecret) {
      return (
        <div className="onboarding-add-credit-card-payment-container">
          <div className="onboarding-add-credit-card-payment-main-div">
            <div className="credit-card-header">
              <p className="heading-h3-semibold color-text-heading-primary">Loading payment form...</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#8139d0",
              colorBackground: "#ffffff",
              colorText: "#7B7B7B",
              colorDanger: "#df1b41",
              fontFamily: "Inter, system-ui, sans-serif",
              spacingUnit: "4px",
              borderRadius: "5px",
              fontSizeBase: "16px",
            },
          },
        }}
      >
        <AddCreditCard
          handleBackToPaymentMethods={handleBackToPaymentMethods}
          customerId={customerId}
          loading={loading}
          setLoading={setLoading}
          navigate={navigate}
          isStepSkippable={isStepSkippable}
          onSkip={onSkip}
        />
      </Elements>
    );
  };

  const handlePaymentMethodSelect = (method) => {
    setRenderView(method);
  };

  const handleBackToPaymentMethods = () => {
    fetchUserInfo();
    setRenderView(ONBOARDING_PAYMENT_VIEW.ADD_PAYMENT_METHOD);
  };

  const fetchUserInfo = (includeSurcharge = false) => {
    const request = includeSurcharge
      ? `customer/${customerId}/info?includeSurcharge=true`
      : `customer/${customerId}/info`;
    dispatch(UserAdditionalInfo(request));
  };

  // Old payment method selection (when stripeFc is OFF)
  const renderOldAddPaymentMethod = () => (
    <div className="onboarding-add-payment-method-container">
      <div className="onboarding-add-payment-method-main-div">
        <div className="payment-method-header">
          <p className="heading-h3-semibold color-text-heading-primary">Add your payment method</p>
        </div>
        <div className="payment-method-options">
          <div
            className="payment-method-card"
            onClick={() => {
              handlePaymentMethodSelect(ONBOARDING_PAYMENT_VIEW.ACH);
              fetchUserInfo();
            }}
          >
            <div className="payment-method-card-content">
              <div className="payment-method-icon">
                <AccountBalanceIcon className="payment-icon" />
              </div>
              <div className="payment-method-info">
                <div className="payment-method-title">
                  <span className="heading-h5-semibold color-text-heading-primary">ACH Direct Debit</span>
                  <CustomChip label="Recommended" variant="filled" size="small" />
                </div>
              </div>
              <ChevronRightIcon className="payment-method-arrow" />
            </div>
          </div>
          <div
            className="payment-method-card-cc"
            onClick={() => {
              handlePaymentMethodSelect(ONBOARDING_PAYMENT_VIEW.CREDIT_CARD);
              fetchUserInfo(true);
            }}
          >
            <div className="payment-method-card-content">
              <div className="payment-method-icon">
                <CreditCardIcon className="payment-icon" />
              </div>
              <div className="payment-method-info">
                <div className="payment-method-title">
                  <span className="heading-h5-semibold color-text-heading-primary">Credit/Debit Card</span>
                  <span className="payment-method-subtitle para-body-s-medium color-text-body-primary">
                    Incl. surcharge
                  </span>
                </div>
              </div>
              <ChevronRightIcon className="payment-method-arrow" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // New payment method selection (when stripeFc is ON)
  const renderNewAddPaymentMethod = () => (
    <div className="onboarding-add-payment-method-container">
      <div className="onboarding-add-payment-method-main-div">
        <div className="payment-options-container">
          <div className="payment-method-header" style={{ padding: "0 20px" }}>
            <p className="heading-h3-semibold color-text-heading-primary">Add your payment method</p>
          </div>

          <div className="payment-options-sections">
            {/* ACH Direct Debit Section */}
            <div className="payment-options-section">
              <div className="payment-options-section-header">
                <p className="payment-options-section-title">ACH direct debit</p>
                <div className="payment-options-divider" />
              </div>

              {/* Connect with Stripe */}
              <div
                className="payment-option-row"
                onClick={() => {
                  fetchUserInfo();
                  handleConnectWithStripeClick();
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    fetchUserInfo();
                    handleConnectWithStripeClick();
                  }
                }}
              >
                <div className="payment-option-icon-circle">
                  <AccountBalanceIcon className="payment-option-icon" />
                </div>
                <div className="payment-option-content">
                  <div className="payment-option-title-row">
                    <span className="payment-option-title">Connect with Stripe</span>
                    {!isMobile && <CustomChip label="Recommended" variant="filled" size="small" />}
                  </div>
                  <p className="payment-option-description">
                    Log in to your bank account to connect it as a payment method.
                  </p>
                  {isMobile && (
                    <div className="payment-option-chip-mobile" style={{ marginTop: "4px" }}>
                      <CustomChip label="Recommended" variant="filled" size="small" />
                    </div>
                  )}
                </div>
                <ChevronRightIcon className="payment-option-chevron" />
              </div>

              {/* Enter Bank Details */}
              <div
                className="payment-option-row"
                onClick={() => {
                  handlePaymentMethodSelect(ONBOARDING_PAYMENT_VIEW.ACH);
                  fetchUserInfo();
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handlePaymentMethodSelect(ONBOARDING_PAYMENT_VIEW.ACH);
                    fetchUserInfo();
                  }
                }}
              >
                <div className="payment-option-icon-circle">
                  <AccountBalanceIcon className="payment-option-icon" />
                </div>
                <div className="payment-option-content">
                  <span className="payment-option-title">Enter bank details</span>
                  <p className="payment-option-description">Manually enter your bank details.</p>
                </div>
                <ChevronRightIcon className="payment-option-chevron" />
              </div>
            </div>

            {/* Other Options Section (Credit Card) */}
            {allowCreditCard && (
              <div className="payment-options-section" style={{ marginTop: "32px" }}>
                <div className="payment-options-section-header">
                  <p className="payment-options-section-title">Other options</p>
                  <div className="payment-options-divider" />
                </div>

                <div
                  className="payment-option-row"
                  onClick={() => {
                    handlePaymentMethodSelect(ONBOARDING_PAYMENT_VIEW.CREDIT_CARD);
                    fetchUserInfo(true);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handlePaymentMethodSelect(ONBOARDING_PAYMENT_VIEW.CREDIT_CARD);
                      fetchUserInfo(true);
                    }
                  }}
                >
                  <div className="payment-option-icon-circle">
                    <CreditCardIcon className="payment-option-icon" />
                  </div>
                  <div className="payment-option-content">
                    <div className="payment-option-title-row">
                      <span className="payment-option-title">Credit / Debit Card</span>
                      {!isMobile && <span className="para-body-s-medium color-text-body-primary">Incl. surcharge</span>}
                    </div>
                    <p className="payment-option-description">
                      Pay with any major credit or debit card. No bank account linking required.
                    </p>
                    {isMobile && (
                      <span className="para-body-s-medium color-text-body-primary" style={{ marginTop: "4px" }}>
                        Incl. surcharge
                      </span>
                    )}
                  </div>
                  <ChevronRightIcon className="payment-option-chevron" />
                </div>
              </div>
            )}
          </div>

          <p className="para-body-s-regular color-text-body-primary" style={{ padding: "0 20px" }}>
            Your payment information is encrypted and securely shared with Stripe for processing. We never store your
            card or bank account information.
          </p>
        </div>
      </div>
    </div>
  );

  const renderPaymentView = () => {
    // When stripeFc is ON, always show options screen first (even without CC)
    if (stripeFc) {
      if (renderView === ONBOARDING_PAYMENT_VIEW.ACH) {
        return (
          <AddAchPayment
            customerId={customerId}
            handleBackToPaymentMethods={handleBackToPaymentMethods}
            loading={loading}
            setLoading={setLoading}
            navigate={navigate}
            isStepSkippable={isStepSkippable}
            onSkip={onSkip}
            setIosPadding={setIosPadding}
          />
        );
      }
      if (renderView === ONBOARDING_PAYMENT_VIEW.CREDIT_CARD) {
        return renderCreditCardForm();
      }
      return renderNewAddPaymentMethod();
    }

    // Old flow: when stripeFc is OFF
    if (!allowCreditCard || renderView === ONBOARDING_PAYMENT_VIEW.ACH) {
      return (
        <AddAchPayment
          customerId={customerId}
          handleBackToPaymentMethods={handleBackToPaymentMethods}
          loading={loading}
          setLoading={setLoading}
          navigate={navigate}
          isStepSkippable={isStepSkippable}
          onSkip={onSkip}
          setIosPadding={setIosPadding}
        />
      );
    }
    if (renderView === ONBOARDING_PAYMENT_VIEW.CREDIT_CARD) {
      return renderCreditCardForm();
    }
    return renderOldAddPaymentMethod();
  };

  return (
    <Layout title="Confirm Your Payment" iosPaddingTop={isIOS && iosPadding ? "105px" : "0px"}>
      {loading && <PageLoader />}
      <div className="onboarding-add-payment-main-div">
        {userInfoLoading ? (
          <PageLoader />
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {isMobile ? (
              <div className="onboarding-sections">
                <div className="onboarding-section onboarding-section--payment">{renderPaymentView()}</div>
                <div className="onboarding-section onboarding-section--breakdown">
                  <PaymentBreakdown view={renderView} />
                </div>
              </div>
            ) : (
              <>
                {renderPaymentView()} <PaymentBreakdown view={renderView} />
              </>
            )}
          </>
        )}
      </div>
      <CustomSnackbar
        open={!!snackbarMessage}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        type="error"
      />
    </Layout>
  );
}

export default AddPaymentMethod;
